import React, { useEffect, useState } from "react";
import { Cookie, X } from "@phosphor-icons/react";

/**
 * Cookie consent banner — the single source of truth for the shopper's
 * Google Consent Mode v2 decision.
 *
 * Contract (Feb 2026, post-Publish 54)
 * ------------------------------------
 * * Default state is DENIED for every signal. `public/index.html`
 *   already primes `gtag('consent','default', { … denied, wait_for_update: 500 })`
 *   before any tag script — this banner never regresses that default.
 * * "Accept" — grants ALL four Google Consent Mode v2 signals
 *   (`ad_storage`, `analytics_storage`, `ad_user_data`,
 *   `ad_personalization`). Reflected in `AnalyticsLoader.jsx` as an
 *   explicit `gtag('consent','update',…)` call and mirrored to Microsoft
 *   Clarity via `applyClarityConsent()`.
 * * "Reject" — keeps every signal denied. Same update path is invoked
 *   with explicit `denied` values so tags that were listening for a
 *   decision unblock immediately (Consent Mode's `wait_for_update`).
 * * The choice is persisted in `localStorage.mps_cookie_consent_v1` with
 *   a version stamp. On subsequent visits `AnalyticsLoader.jsx` re-reads
 *   the record on mount and re-issues the `gtag('consent','update',…)`
 *   payload so the shopper's prior decision continues to apply without
 *   showing the banner again.
 */

const STORAGE_KEY = "mps_cookie_consent_v1";

export const CookieConsent = () => {
  const [choice, setChoice] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setChoice(parsed);
        window.__mps_consent = parsed;
      }
    } catch (err) {
      // Corrupt or unavailable localStorage — treat as no prior consent.
      if (process.env.NODE_ENV !== "production") console.warn("CookieConsent: failed to read stored consent", err);
    }
  }, []);

  const save = (payload) => {
    const record = { ...payload, at: new Date().toISOString(), v: 1 };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(record)); }
    catch (_) { /* Safari private mode / storage quota — still emit the event below. */ }
    setChoice(record);
    if (typeof window !== "undefined") window.__mps_consent = record;
    // Notify listeners (AnalyticsLoader) so they can react without a reload.
    try {
      window.dispatchEvent(new CustomEvent("mps:consent-updated", { detail: record }));
    } catch (err) {
      // Older browsers without CustomEvent constructor — safe to ignore.
    }
    setDismissed(true);
  };

  const acceptAll = () =>
    // Analytics + marketing both granted — AnalyticsLoader will emit
    // gtag('consent','update',{ ad_storage, analytics_storage,
    // ad_user_data, ad_personalization : granted }) for us.
    save({ essential: true, analytics: true, marketing: true });

  const rejectAll = () =>
    // Every signal stays denied. AnalyticsLoader emits an explicit
    // deny update so Consent Mode's wait_for_update unblocks.
    save({ essential: true, analytics: false, marketing: false });

  if (choice || dismissed) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie preferences"
      data-testid="cookie-consent"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-50 border border-brand-border bg-white/95 backdrop-blur-md p-5"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 mb-2 overline">
          <Cookie size={14} weight="duotone" className="text-brand-accent" /> Cookie preferences
        </div>
        <button onClick={() => setDismissed(true)} aria-label="Close" className="text-brand-muted hover:text-brand-text" data-testid="consent-close">
          <X size={14} />
        </button>
      </div>
      <p className="text-xs text-brand-muted leading-relaxed" data-testid="consent-copy">
        We use essential cookies for authentication and cart persistence. With your
        permission we also use Google Analytics 4, Google Ads (to measure ad
        performance), and Microsoft Clarity (anonymous heatmaps and session
        recordings) to improve the catalog. Checkout, payment, account, admin,
        and every form area are masked. See our{" "}
        <a href="/cookie-policy" className="underline hover:text-brand-accent">Cookie Policy</a>{" "}and{" "}
        <a href="/privacy" className="underline hover:text-brand-accent">Privacy Policy</a>{" "}for details.
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          onClick={acceptAll}
          data-testid="consent-accept-all"
          className="flex-1 bg-brand-navy text-white hover:bg-brand-navy2 font-semibold px-4 py-2 text-xs tracking-wide"
        >
          Accept
        </button>
        <button
          onClick={rejectAll}
          data-testid="consent-essential-only"
          className="flex-1 border border-brand-border text-brand-text hover:border-brand-accent hover:text-brand-accent font-semibold px-4 py-2 text-xs tracking-wide"
        >
          Reject
        </button>
      </div>
    </div>
  );
};
